import React, { useCallback, useMemo, useState } from 'react';

import { Alert, Box } from '@mui/material';

import { ExtranetPendingChange } from '~/services/ExtranetService';

import BulkActionBar from './BulkActionBar';
import ChangeDiffDialog from './ChangeDiffDialog';
import ConfirmActionDialog from './ConfirmActionDialog';
import ExtranetApprovalsPageHeader from './ExtranetApprovalsPageHeader';
import ExtranetApprovalsTable from './ExtranetApprovalsTable';
import ExtranetApprovalsTabs from './ExtranetApprovalsTabs';
import FilterToolbar from './FilterToolbar';
import ReplayFailureAlert from './ReplayFailureAlert';
import useExtranetApprovalActions from './hooks/useExtranetApprovalActions';
import useExtranetApprovalsData from './hooks/useExtranetApprovalsData';
import useExtranetApprovalsSelection from './hooks/useExtranetApprovalsSelection';
import useExtranetApprovalsUrlState from './hooks/useExtranetApprovalsUrlState';
import useExtranetChangeMetadata from './hooks/useExtranetChangeMetadata';

export default function ExtranetApprovalsPage() {
  const urlState = useExtranetApprovalsUrlState();
  const { counts, error, isLoading, rows, total } = useExtranetApprovalsData(urlState.appliedFilters);
  const selectableRows = useMemo(() => (urlState.isPendingTab ? rows : []), [rows, urlState.isPendingTab]);
  const selection = useExtranetApprovalsSelection(selectableRows);
  const actions = useExtranetApprovalActions({ onSuccess: selection.clearSelection });

  const [diffRow, setDiffRow] = useState<ExtranetPendingChange | null>(null);

  const visibleChanges = useMemo(() => {
    const changes = [...rows];
    if (urlState.deepLinkedChange && !changes.some((row) => row.id === urlState.deepLinkedChange?.id)) {
      changes.push(urlState.deepLinkedChange);
    }
    return changes;
  }, [rows, urlState.deepLinkedChange]);

  const metadataByChangeId = useExtranetChangeMetadata(visibleChanges);

  const activeDiffRow = diffRow ?? urlState.deepLinkedDiffRow;

  const handleSearch = useCallback(() => {
    selection.clearSelection();
    urlState.handleSearch();
  }, [selection, urlState]);

  const handleClear = useCallback(() => {
    selection.clearSelection();
    urlState.handleClear();
  }, [selection, urlState]);

  const handleTabChange = useCallback(
    (tab: number) => {
      selection.clearSelection();
      urlState.changeTab(tab);
    },
    [selection, urlState],
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      selection.clearSelection();
      urlState.changePage(nextPage);
    },
    [selection, urlState],
  );

  return (
    <Box>
      <ExtranetApprovalsPageHeader pendingCount={counts.pending} homesVillasPendingCount={counts.homesVillasPending} />

      <ExtranetApprovalsTabs activeTab={urlState.activeTab} onChange={handleTabChange} />

      <FilterToolbar
        filters={urlState.filters}
        setFilters={urlState.setFilters}
        showRequestTypeFilter={urlState.isHistoryTab}
        showStatusFilter={urlState.isHistoryTab}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      <BulkActionBar
        selectedCount={selection.selectedIds.length}
        isMutating={actions.isMutating}
        onBulkApprove={() => actions.setConfirmAction({ type: 'approve', ids: selection.selectedIds })}
        onBulkReject={() => actions.setConfirmAction({ type: 'reject', ids: selection.selectedIds })}
      />

      {error instanceof Error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      {actions.replayFailures.length > 0 && (
        <ReplayFailureAlert failures={actions.replayFailures} onClose={() => actions.setReplayFailures([])} />
      )}

      <ExtranetApprovalsTable
        rows={rows}
        total={total}
        page={urlState.page}
        isLoading={isLoading}
        isPendingTab={urlState.isPendingTab}
        isHistoryTab={urlState.isHistoryTab}
        isMutating={actions.isMutating}
        metadataByChangeId={metadataByChangeId}
        selected={selection.selected}
        allSelected={selection.allSelected}
        onToggleSelect={selection.toggleSelect}
        onToggleSelectAll={selection.toggleSelectAll}
        onViewDiff={setDiffRow}
        onRowAction={(type, id) => actions.setConfirmAction({ type, ids: [id] })}
        onPageChange={handlePageChange}
      />

      <ChangeDiffDialog
        change={activeDiffRow}
        metadata={activeDiffRow ? metadataByChangeId.get(activeDiffRow.id) : undefined}
        onClose={() => {
          setDiffRow(null);
          urlState.clearDeepLinkedDiffRow();
        }}
        onApprove={(id) => {
          actions.setConfirmAction({ type: 'approve', ids: [id] });
          setDiffRow(null);
          urlState.clearDeepLinkedDiffRow();
        }}
        onReject={(id) => {
          actions.setConfirmAction({ type: 'reject', ids: [id] });
          setDiffRow(null);
          urlState.clearDeepLinkedDiffRow();
        }}
      />

      <ConfirmActionDialog
        action={actions.confirmAction}
        isMutating={actions.isMutating}
        onClose={() => actions.setConfirmAction(null)}
        onConfirm={actions.handleConfirmAction}
      />
    </Box>
  );
}
